import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { CButton, CDialog } from '@appcues/component-library';
import { ExternalLink } from '@studio/legacy-components';
import {
  SINGLE_SIGN_ON,
  PASSWORDLESS_INVITATIONS,
} from 'next/entities/features';
import { USER_SEATS_ENTITLEMENT } from 'constants/entitlements';

import { readOne as fetchAccountEntitlement } from 'actions/entitlements';
import { selectIsEntitlementSynced } from 'reducers/entitlements';
import { selectIsAllowedToInviteUsers } from 'selectors/entitlements';
import CoolTip from 'components/Common/CoolTip';
import Loader from 'components/Common/Loader';
import { asPromised } from 'utils/as-promised';
import { inviteUser } from 'actions/account/users';
import { selectAccountFeature } from 'reducers/account/features';
import { selectAccountInfo } from 'reducers/account/meta';
import { EDITOR_PERMISSIONS_ROLE } from 'constants/features';

import InviteTeammate from './InviteTeammate';

const StyledCDialog = styled(CDialog)`
  & > div {
    overflow-y: auto;
  }
`;

export const InviteManager = ({
  onInvite,
  disabled = false,
  enableEditorRole = false,
  hasSSO = false,
  hasPasswordlessInvitations = false,
  discoverable,
  isSynced = false,
  onLoad,
}) => {
  const [currentModal, setCurrentModal] = useState(null);
  const [invitedUser, setInvitedUser] = useState(null);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const clearCurrentModal = useCallback(
    () => setCurrentModal(null),
    [setCurrentModal]
  );

  const handleInviteUser = useCallback(
    fields => {
      setInvitedUser(fields);

      const invitation = {
        ...fields,
        singleSignOn: hasSSO,
        passwordlessInvitation: hasPasswordlessInvitations,
      };

      return onInvite(invitation)
        .then(() => setCurrentModal('INVITE_SENT'))
        .catch(error => {
          switch (error.name) {
            case 'UserInAccountError':
              setCurrentModal('USER_IN_ACCOUNT');
              break;
            case 'MaxUsersError':
              setCurrentModal('MAX_USERS_REACHED');
              break;
            default:
              setCurrentModal('ERROR');
          }
        });
    },
    [onInvite]
  );

  if (!isSynced) {
    return <Loader />;
  }

  return (
    <>
      <InviteTeammate
        enableEditorRole={enableEditorRole}
        hasSSO={hasSSO}
        hasPasswordlessInvitations={hasPasswordlessInvitations}
        discoverable={discoverable}
        onClose={clearCurrentModal}
        onSubmit={handleInviteUser}
        visible={currentModal === 'INVITE'}
      />
      {currentModal === 'USER_IN_ACCOUNT' && (
        <StyledCDialog
          title={`${
            invitedUser.name || invitedUser.email
          } is already in this account`}
          onPrimaryAction={clearCurrentModal}
        >
          Whoops! We found a user with that email in your account already! Let
          us know if they are having trouble logging in.
        </StyledCDialog>
      )}
      {currentModal === 'MAX_USERS_REACHED' && (
        <StyledCDialog
          title="Account Limit Reached"
          onPrimaryAction={clearCurrentModal}
        >
          You have reached the maximum number of users supported by the Free
          Trial. To add more users, please email{' '}
          <ExternalLink href="mailto:sales@appcues.com">
            sales@appcues.com
          </ExternalLink>{' '}
          to upgrade.
        </StyledCDialog>
      )}
      {currentModal === 'INVITE_SENT' && (
        <StyledCDialog
          title={`${
            invitedUser.name || invitedUser.email
          } has been invited to join your account`}
          onPrimaryAction={clearCurrentModal}
        >
          They will receive an e-mail to this effect with login instructions.
        </StyledCDialog>
      )}
      {currentModal === 'ERROR' && (
        <StyledCDialog
          title="We encountered an error"
          onPrimaryAction={clearCurrentModal}
        >
          Sorry about that. Please try again, and if the problem persists,
          don&apos;t hesitate to contact us.
        </StyledCDialog>
      )}
      <CoolTip
        tip="Only account admins can invite new users"
        disabled={!disabled}
      >
        <CButton
          type="positive"
          disabled={!disabled}
          onClick={() => setCurrentModal('INVITE')}
        >
          Invite teammate
        </CButton>
      </CoolTip>
    </>
  );
};

InviteManager.propTypes = {
  onInvite: PropTypes.func,
  disabled: PropTypes.bool,
  enableEditorRole: PropTypes.bool,
  onLoad: PropTypes.func,
};

const mapStateToProps = state => {
  const accountInfo = selectAccountInfo(state);

  return {
    enableEditorRole: selectAccountFeature(state, EDITOR_PERMISSIONS_ROLE),
    hasSSO: selectAccountFeature(state, SINGLE_SIGN_ON),
    hasPasswordlessInvitations: selectAccountFeature(
      state,
      PASSWORDLESS_INVITATIONS
    ),
    disabled: selectIsAllowedToInviteUsers(state),
    discoverable: accountInfo.sso_discoverability_enabled,
    isSynced: selectIsEntitlementSynced(state, USER_SEATS_ENTITLEMENT),
  };
};

const mapDispatchToProps = dispatch => ({
  onInvite: fields => asPromised(dispatch, inviteUser(fields)),
  onLoad: () => fetchAccountEntitlement(USER_SEATS_ENTITLEMENT),
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteManager);
